import axios from 'axios';

/**
 * Frontend OTP Helper Utility
 * Provides reusable functions for checking OTP status in components
 */

let otpSettingsCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Check if OTP is enabled for a specific component
 * Uses cache to minimize API calls
 * 
 * @param {string} componentName - Name of the component (e.g., 'StudentForm', 'FeePayment')
 * @param {boolean} bypassCache - Force fetch from server ignoring cache (default: false)
 * @returns {Promise<boolean>} - true if OTP is enabled, false otherwise
 * 
 * @example
 * const otpEnabled = await isOtpEnabledForComponent('StudentForm');
 * if (otpEnabled) {
 *   handleGetOtp();
 * } else {
 *   handleSave();
 * }
 */
export const isOtpEnabledForComponent = async (componentName, bypassCache = false) => {
  try {
    // Check cache first
    if (!bypassCache && otpSettingsCache[componentName]) {
      const cached = otpSettingsCache[componentName];
      const now = Date.now();
      
      if (now - cached.timestamp < CACHE_DURATION) {
        console.log(`Using cached OTP status for ${componentName}:`, cached.isEnabled);
        return cached.isEnabled;
      }
    }

    // Fetch from API
    const instituteId = localStorage.getItem('instituteId');
    const params = instituteId ? { institute_id: instituteId } : {};
    const response = await axios.get(`/api/otp-settings/${componentName}`, { 
      params,
      withCredentials: true 
    });
    const isEnabled = response.data.is_otp_enabled ?? true; // Default to true if not specified

    // Cache the result
    otpSettingsCache[componentName] = {
      isEnabled,
      timestamp: Date.now()
    };

    console.log(`Fetched OTP status for ${componentName}:`, isEnabled);
    return isEnabled;
  } catch (error) {
    console.error(`Error checking OTP status for ${componentName}:`, error);
    // Default to true (OTP enabled) on error
    return true;
  }
};

/**
 * Get all OTP settings
 * 
 * @param {boolean} bypassCache - Force fetch from server ignoring cache (default: false)
 * @returns {Promise<Object>} - Object with component names as keys and boolean status as values
 * 
 * @example
 * const allSettings = await getAllOtpSettings();
 * console.log(allSettings);
 * // { StudentForm: true, FeePayment: false, ... }
 */
export const getAllOtpSettings = async (bypassCache = false) => {
  try {
    // Check cache first
    const cacheKey = '__ALL_SETTINGS__';
    if (!bypassCache && otpSettingsCache[cacheKey]) {
      const cached = otpSettingsCache[cacheKey];
      const now = Date.now();
      
      if (now - cached.timestamp < CACHE_DURATION) {
        console.log('Using cached OTP settings');
        return cached.settings;
      }
    }

    // Fetch from API
    const response = await axios.get('/api/otp-settings', { withCredentials: true });
    
    // Convert array to object
    const settings = {};
    response.data.forEach(setting => {
      settings[setting.component_name] = setting.is_otp_enabled;
    });

    // Cache the result
    otpSettingsCache[cacheKey] = {
      settings,
      timestamp: Date.now()
    };

    console.log('Fetched all OTP settings:', settings);
    return settings;
  } catch (error) {
    console.error('Error fetching all OTP settings:', error);
    // Return empty object on error
    return {};
  }
};

/**
 * Clear the OTP cache
 * 
 * @param {string} componentName - Specific component to clear cache for (optional)
 * 
 * @example
 * // Clear cache for specific component
 * clearOtpCache('StudentForm');
 * 
 * // Clear all cache
 * clearOtpCache();
 */
export const clearOtpCache = (componentName = null) => {
  if (componentName) {
    delete otpSettingsCache[componentName];
    console.log(`Cleared cache for ${componentName}`);
  } else {
    otpSettingsCache = {};
    console.log('Cleared all OTP cache');
  }
};

/**
 * Hook-friendly wrapper for checking OTP status
 * Can be used with useEffect
 * 
 * @param {string} componentName - Name of the component
 * @param {Function} callback - Callback function(isEnabled)
 * @param {Array} dependencies - Dependencies array for useEffect
 * 
 * @example
 * useOtpStatus('StudentForm', (isEnabled) => {
 *   setOtpRequired(isEnabled);
 * }, []);
 */
export const useOtpStatus = async (componentName, callback, dependencies = []) => {
  const isEnabled = await isOtpEnabledForComponent(componentName);
  if (callback) {
    callback(isEnabled);
  }
};

/**
 * Batch check multiple components OTP status
 * 
 * @param {Array<string>} componentNames - Array of component names
 * @returns {Promise<Object>} - Object with component names as keys
 * 
 * @example
 * const statuses = await checkMultipleComponents(['StudentForm', 'FeePayment']);
 * // { StudentForm: true, FeePayment: false }
 */
export const checkMultipleComponents = async (componentNames) => {
  try {
    const results = {};
    
    for (const componentName of componentNames) {
      results[componentName] = await isOtpEnabledForComponent(componentName);
    }
    
    return results;
  } catch (error) {
    console.error('Error checking multiple components:', error);
    // Return all as true on error
    const results = {};
    componentNames.forEach(name => {
      results[name] = true;
    });
    return results;
  }
};

export default {
  isOtpEnabledForComponent,
  getAllOtpSettings,
  clearOtpCache,
  useOtpStatus,
  checkMultipleComponents
};
